import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

import SellIcon from '@mui/icons-material/Sell';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BarChartIcon from '@mui/icons-material/BarChart';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const SECTION_ICON = {
  category: SellIcon,
  location: LocationOnIcon,
  engagement_rate: BarChartIcon,
  reach: GroupIcon,
  keywords: SearchIcon,
};

const BADGE_STYLE = {
  score:   { bg: '#eff6ff', color: '#1d4ed8', border: '1.5px solid #bfdbfe' },
  success: { bg: '#f0fdf4', color: '#15803d', border: '1.5px solid #bbf7d0' },
  warning: { bg: '#fffbeb', color: '#92400e', border: '1.5px solid #fcd34d' },
  error:   { bg: '#fef2f2', color: '#991b1b', border: '1.5px solid #fecaca' },
};

const SectionBadge = ({ badge }) => {
  if (!badge) return null;
  const s = BADGE_STYLE[badge.style] || BADGE_STYLE.score;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.2,
        borderRadius: '999px',
        background: s.bg,
        border: s.border,
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: s.color, lineHeight: 1.4 }}>
        {badge.label}
      </Typography>
    </Box>
  );
};

const ItemRow = ({ item, maxPts }) => {
  // Scored item — label + blue progress bar + pts
  if (item.pts != null) {
    const pct = Math.min(100, maxPts > 0 ? (item.pts / maxPts) * 100 : 0);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6, pl: 1.25 }}>
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#cbd5e1', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '11.5px', color: '#374151', flex: 1, lineHeight: 1.4 }}>
          {item.label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          <Box sx={{ width: 56, height: 5, borderRadius: 3, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: '#3b82f6', borderRadius: 3 }} />
          </Box>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1e3a5f', minWidth: 38, textAlign: 'right' }}>
            {item.pts} pts
          </Typography>
        </Box>
      </Box>
    );
  }

  // Stat row — label + right-aligned value
  if (item.value != null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6, pl: 1.25 }}>
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#cbd5e1', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '11.5px', color: '#374151', flex: 1, lineHeight: 1.4 }}>
          {item.label}
        </Typography>
        <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: '#111827' }}>
          {item.value}
        </Typography>
      </Box>
    );
  }

  // Warning / drift / alert row
  const isWarn = item.style === 'warning' || item.style === 'error';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.6, pl: 1.25 }}>
      {isWarn ? (
        <WarningAmberIcon sx={{ fontSize: 13, color: '#f59e0b', flexShrink: 0, mt: '2px' }} />
      ) : (
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#cbd5e1', flexShrink: 0, mt: '6px' }} />
      )}
      <Typography sx={{ fontSize: '11.5px', color: isWarn ? '#92400e' : '#374151', flex: 1, lineHeight: 1.4 }}>
        {item.label}
      </Typography>
      {item.badge && (
        <Box
          sx={{
            px: 0.75,
            py: 0.2,
            borderRadius: '6px',
            bgcolor: '#fffbeb',
            border: '1px solid #fcd34d',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#92400e', lineHeight: 1.4 }}>
            {item.badge}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const SearchReasonCard = ({ reason }) => {
  if (!reason) return null;

  // Plain string
  if (typeof reason === 'string') {
    return (
      <Typography sx={{ fontSize: '13px', color: '#1f2433', lineHeight: 1.6 }}>
        {reason}
      </Typography>
    );
  }

  // Old flat object — simple key-value rows
  if (!reason.sections) {
    return (
      <Box>
        {Object.entries(reason).map(([key, value]) => (
          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.75 }}>
            <Typography sx={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {key}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#1f2433', textAlign: 'right' }}>
              {String(value)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  const { sections = [], overall } = reason;

  return (
    <Box sx={{ minWidth: 300 }}>
      {sections.map((section, idx) => {
        const Icon = SECTION_ICON[section.id] || SellIcon;
        const badge = section.badge || {};

        // Parse section max from badge label like "35 / 40 pts"
        let maxPts = 100;
        if (badge.style === 'score' && badge.label) {
          const m = badge.label.match(/\/\s*(\d+)/);
          if (m) maxPts = parseInt(m[1], 10);
        }

        return (
          <Box key={section.id}>
            {idx > 0 && <Divider sx={{ my: 1.25 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <Icon sx={{ fontSize: 14, color: '#374151' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '12.5px', color: '#111827' }}>
                {section.title}
              </Typography>
              <SectionBadge badge={badge} />
            </Box>
            {(section.items || []).map((item, i) => (
              <ItemRow key={i} item={item} maxPts={maxPts} />
            ))}
          </Box>
        );
      })}

      {overall && (
        <>
          <Divider sx={{ my: 1.25 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '11.5px', color: '#6b7280' }}>Overall</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {overall.es_score != null && (
                <Typography sx={{ fontSize: '11.5px', color: '#374151' }}>
                  ES <strong>{overall.es_score}</strong>
                </Typography>
              )}
              {overall.relevancy != null && (
                <Typography sx={{ fontSize: '11.5px', color: '#374151' }}>
                  Relevancy <strong>{overall.relevancy} / 10</strong>
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SearchReasonCard;
